import { Highlighter } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const FAQ_ITEMS: { question: string; answer: string }[] = [
  {
    question: 'What is a highlight?',
    answer:
      'A highlight is a passage from an article, note, or web page that someone found worth marking — like swiping a yellow marker across a book. On glowmark, highlights are Nostr events (NIP-84): they contain the quoted text, a link to the source, and credit to the original author.',
  },
  {
    question: 'What is glowmark?',
    answer:
      'glowmark is a reading client dedicated to highlights. It collects the passages people mark across Nostr and presents them as a calm reading feed — the best bits of everything being read, surfaced by real humans instead of an algorithm.',
  },
  {
    question: 'Where are my highlights stored?',
    answer:
      'On Nostr relays, signed by your private key. glowmark never holds your data — your highlights are public, portable events that any Nostr app can read. Deleting a highlight sends a deletion request to your relays; most will remove it.',
  },
  {
    question: 'How do I create a highlight?',
    answer:
      'Log in, then either: select any text while reading an article or note in glowmark and click the floating "Highlight" button — or open any web page in glowmark\'s reader (paste its URL in the open bar, then hit "Read") and select passages there. For web pages, glowmark automatically looks for the author\'s Nostr identity (a rel="me" link on the page) so they get credited and can receive zaps.',
  },
  {
    question: 'What is a quote highlight?',
    answer:
      'A highlight with your own commentary attached. Add a comment in the composer before publishing and your thoughts will appear above the quoted passage, like a quote post.',
  },
  {
    question: 'How do I open a specific article, note, or page?',
    answer:
      'Use the search icon in the top menu (or the bar on the home page). You can paste a Nostr identifier (naddr1…, note1…, npub1…), a nostr: link, a link from another Nostr client, or a plain web URL to see highlights of that page.',
  },
  {
    question: 'What are the Global, Following, and Mine tabs?',
    answer:
      'Global is the public firehose of highlights from everyone on Nostr. Following shows highlights from people you follow (log in first). Mine is your personal notebook — every passage you have ever marked.',
  },
  {
    question: "What if the author isn't on Nostr yet?",
    answer:
      'Most aren\'t — yet. Highlights of their work still accumulate publicly, and zaps default to the highlighter who surfaced the passage. There\'s no honest way to hold money for a future author without someone custodially holding it, which glowmark deliberately doesn\'t do. Instead, every uncredited highlight is an invitation: its web page shows an "Is this your page?" panel with the one <link rel="me"> tag that makes the author payable, and after publishing you get a ready-made invite message to send them. Once they\'re on, all future highlights credit them automatically.',
  },
  {
    question: 'Can someone falsely credit the wrong author?',
    answer:
      'They can write the claim, but it won\'t pay. For Nostr-native articles and notes, the author is cryptographically proven by the source event\'s signature — no tag can override it. For web pages, glowmark re-verifies author claims before any money moves: the page must declare the claimed identity via rel="me", or the author\'s NIP-05 domain must match the page and vouch for the key. Unverified claims fall back to paying the highlighter, clearly labeled.',
  },
  {
    question: 'What do the heart, comment, and zap buttons do?',
    answer:
      'They let you react to a highlight, join its discussion thread, or send sats as thanks. When a highlight credits an original author, zaps go to them — the person who wrote the words earns from them, with a share for whoever marked the passage. Every zap leaves a public receipt on the event, so a highlight embedded on ten sites shows the same accumulating total everywhere. Connect a wallet via the wallet icon in the top menu (Nostr Wallet Connect or a browser wallet).',
  },
];

interface FaqDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Brief in-app FAQ, opened from the header question-mark button. */
export function FaqDialog({ open, onOpenChange }: FaqDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Highlighter className="size-4.5 text-primary" />
            Frequently asked questions
          </DialogTitle>
          <DialogDescription>The basics of glowmark and Nostr highlights.</DialogDescription>
        </DialogHeader>

        <Accordion type="single" collapsible className="w-full">
          {FAQ_ITEMS.map((item, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </DialogContent>
    </Dialog>
  );
}
